import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';

import { revokeRefreshToken } from '@/lib/auth-token-cache';
import { clearPurchaseIntentHint } from '@/lib/purchase-intent-hint';
import { clearSelectedPlan } from '@/lib/selected-plan';
import { forgetTenantScopedStorage } from '@/lib/tenant-storage';

/**
 * The one way out of the application.
 *
 * Every sign-out — the user asking for it, declining the EULA, or the inactivity
 * auto-logoff — owes the same three things, and each one was previously spelled
 * out again at its own call site (one of which forgot the second):
 *
 * 1. revoke the refresh token, so the credential does not outlive the session
 *    (ESCLOUD-112);
 * 2. forget what this browser remembers about the tenant and about any pending
 *    purchase (ESCLOUD-332), so the next user of a shared clinical workstation
 *    does not inherit - or resume - them;
 * 3. end the Auth0 session.
 *
 * The revocation is deliberately not awaited: it is sent with `keepalive`, so it
 * survives the navigation, and a slow or failed Auth0 must not delay or block a
 * user leaving.
 */
export function useSignOut(): () => void {
    const { logout } = useAuth0();

    return useCallback(() => {
        void revokeRefreshToken();
        forgetTenantScopedStorage();
        clearPurchaseIntentHint();
        clearSelectedPlan();
        logout({ logoutParams: { returnTo: window.location.origin } });
    }, [logout]);
}
